import { create } from 'zustand';
import { getHandoffSummary } from '../lib/services/customers/humanAgentApi';

/**
 * CS 2.0 需求 13 — how many handed-off conversations are still waiting for the team's first reply,
 * and how many of those are urgent.
 *
 * <p><b>One copy, for every surface that shows it.</b> The sidebar's Support and Anna rows, the Home
 * Customer Support card and Anna Chat's header all read this store, so they cannot show two numbers
 * from two moments. Nothing here decides WHEN to refresh — `useHandoffSummarySource` (mounted once,
 * in the dashboard layout) does that; a page that knows it just changed the answer (the merchant
 * replied, or handed a conversation back) calls {@link refreshHandoffSummary} directly.
 *
 * <p><b>The count carries its owner.</b> Accounts switch inside one tab, and a count fetched for the
 * previous merchant must never be drawn for the next one — readers go through
 * {@link useHandoffSummary}, which answers zero until the count for THIS user has landed.
 *
 * <p>A failed read keeps the last good count rather than dropping to zero: this is polled, and one
 * network blip clearing a red dot that is genuinely there (and relighting it a minute later) reads as
 * a bug in the one signal meant to say "someone is waiting".
 */

export interface HandoffSummaryState {
  /** Whose count this is. Null until the first read for anyone has landed. */
  userId: string | null;
  unreplied: number;
  urgent: number;
  /** Fetch the count for this user now. Overlapping calls collapse; a stale answer is dropped. */
  refresh: (userId: string) => Promise<void>;
  /** Forget everything (sign-out, or a merchant without Anna). */
  reset: () => void;
}

/**
 * Bumped on every request and on reset, so an answer that lands after a newer request (or after the
 * user changed) is ignored instead of overwriting a fresher one.
 */
let sequence = 0;
/** The user the most recent refresh was for — what {@link refreshHandoffSummary} re-reads. */
let lastUserId: string | null = null;

export const useHandoffSummaryStore = create<HandoffSummaryState>(set => ({
  userId: null,
  unreplied: 0,
  urgent: 0,
  refresh: async (userId: string) => {
    if (!userId) return;
    lastUserId = userId;
    const mine = ++sequence;
    try {
      const summary = await getHandoffSummary(userId);
      if (mine !== sequence) return;
      set({ userId, unreplied: summary.unreplied, urgent: Math.min(summary.urgent, summary.unreplied) });
    } catch {
      // Keep the last good count (see the class doc). A different user's count is not "last good"
      // for this one, so that case resets.
      if (mine !== sequence) return;
      set(state => (state.userId === userId ? state : { userId, unreplied: 0, urgent: 0 }));
    }
  },
  reset: () => {
    sequence += 1;
    lastUserId = null;
    set({ userId: null, unreplied: 0, urgent: 0 });
  },
}));

/**
 * Re-read the count for whoever it was last read for. For the moments a page KNOWS the answer just
 * changed — the merchant's first reply in a handed-off conversation, or "Hand back to Anna" — so the
 * dot clears now rather than at the next poll. A no-op before the first read.
 */
export function refreshHandoffSummary(): void {
  if (lastUserId) void useHandoffSummaryStore.getState().refresh(lastUserId);
}

/** The count for this user, or zeros while it is not known (yet, or any more). */
export function useHandoffSummary(userId: string | null | undefined): {
  unreplied: number;
  urgent: number;
} {
  const owner = useHandoffSummaryStore(s => s.userId);
  const unreplied = useHandoffSummaryStore(s => s.unreplied);
  const urgent = useHandoffSummaryStore(s => s.urgent);
  return userId && owner === userId ? { unreplied, urgent } : { unreplied: 0, urgent: 0 };
}
